/**
 * 74.13 step 4 (ARCHITECTURE §5.47; DECISIONS 374 (3), 376): the landforms' ground records (`PlanetState.shapings`: the cliff
 * bands along the rims, the crests on a coarse world — core/landforms.ts featureShapings) as the fine ground reads them. Each
 * is 71.5's record (core/shaping.ts's rule), all `raise` ribbons: they only ever hold the ground up, so they compose in any
 * order; they are applied in the list's order (ascending ids), under the terraces (a pad's level wins over a cliff) and the
 * decks. Filed in a cell index by direction (core/bumpIndex.ts), so a lookup reads a cell's few records. Laid once at
 * generation and never edited (setup only drops the ones a structure stands in — `clearShapingsUnder`), so every cache keyed
 * on the planet object holds. Off (no records), nothing here runs. Pure; no trig beyond the index's cosines.
 */
import type { PlanetState, Shaping, Vec3 } from '../types';
import { bumpIndex, type BumpIndex } from './bumpIndex';
import { shapedAt } from './shaping';
import { dcos } from './dmath';

/** A planet's records filed for lookup (memoised on the list): read by the fine ground, sight (core/fineTerrain.ts shapingSegmentBlocked) and setup's clearing (core/landforms.ts clearShapingsUnder). */
export interface ShapingCells {
  radius: number;
  /** per record: its centre (a unit direction), the cosine of its extent's angle, its highest level (an absolute radius) */
  centres: Array<{ dir: Vec3 }>; cosExtent: Float64Array; extent: Float64Array; top: Float64Array;
  /** the widest extent (world) and the index filed at it; the highest level of all */
  reach: number; index: BumpIndex; highest: number;
}
const cellsMemo = new WeakMap<readonly Shaping[], ShapingCells>();

/** The records filed: each one's centre and how far from it (world) it reaches — its spine's farthest node, its half width and its reach. */
export function cellsOf(list: readonly Shaping[], radius: number): ShapingCells {
  const kept = cellsMemo.get(list);
  if (kept && kept.radius === radius) return kept;
  const n = list.length, centres: Array<{ dir: Vec3 }> = [], cosExtent = new Float64Array(n), extent = new Float64Array(n), top = new Float64Array(n);
  let reach = 0, highest = -Infinity;
  for (let i = 0; i < n; i++) {
    const s = list[i], f = s.footprint;
    let cx = 0, cy = 0, cz = 0, far = 0, hi = -Infinity, half: number;
    if (f.kind === 'pad') { cx = f.dir.x; cy = f.dir.y; cz = f.dir.z; far = Math.sqrt(f.halfX * f.halfX + f.halfZ * f.halfZ); hi = f.level; half = 0; }
    else {
      for (const nd of f.nodes) { cx += nd.dir.x; cy += nd.dir.y; cz += nd.dir.z; if (nd.level > hi) hi = nd.level; }
      half = f.half;
    }
    const l = Math.sqrt(cx * cx + cy * cy + cz * cz) || 1;
    cx /= l; cy /= l; cz /= l;
    if (f.kind === 'ribbon') for (const nd of f.nodes) { const ex = nd.dir.x - cx, ey = nd.dir.y - cy, ez = nd.dir.z - cz; far = Math.max(far, Math.sqrt(ex * ex + ey * ey + ez * ez) * radius); }
    const e = far + half + s.reach;
    centres.push({ dir: { x: cx, y: cy, z: cz } }); extent[i] = e; cosExtent[i] = dcos(Math.min(Math.PI, e / radius)) - 1e-12; top[i] = hi;
    if (e > reach) reach = e;
    if (hi > highest) highest = hi;
  }
  const out: ShapingCells = { radius, centres, cosExtent, extent, top, reach, index: bumpIndex(centres, radius, reach), highest };
  cellsMemo.set(list, out);
  return out;
}

/** The highest level a planet's records hold the ground to (an absolute radius), or −Infinity without records: the ground's ceiling reads it. */
export function shapingsHighest(planet: Pick<PlanetState, 'shapings' | 'radius'>): number {
  return planet.shapings?.length ? cellsOf(planet.shapings, planet.radius).highest : -Infinity;
}

/**
 * The fine ground's read: the ground `g` (a radius: the mesh and the relief) at the unit direction (ux, uy, uz) with the
 * planet's records applied — each that reaches it, in the list's order.
 */
export function shapedGround(planet: Pick<PlanetState, 'shapings' | 'radius'>, ux: number, uy: number, uz: number, g: number): number {
  const list = planet.shapings;
  if (!list?.length) return g;
  const c = cellsOf(list, planet.radius), near = c.index.at(ux, uy, uz);
  for (let q = 0; q < near.length; q++) {
    const i = near[q], cd = c.centres[i].dir;
    if (ux * cd.x + uy * cd.y + uz * cd.z < c.cosExtent[i]) continue;
    g = shapedAt(list[i], planet.radius, ux, uy, uz, g);
  }
  return g;
}

